import { useMemo } from "react";
import {
  compareInSeason,
  compareUpcoming,
  compareFinished,
} from "./dashboardViewModel";
import type { SelectableDashboardView } from "./DashboardViewSwitch";
import { LeagueRow } from "./LeagueRow";
import { Pager } from "./Pager";
import { DASHBOARD_CARD_CLASS } from "./dashboardStyles";
import type { Clubhouse_LeagueOperatorDashboardLeagueDto } from "../../types/leagues";

interface DashboardBucketListProps {
  view: SelectableDashboardView;
  leagues: Clubhouse_LeagueOperatorDashboardLeagueDto[];
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onOpen: (leagueId: string) => void;
  onDelete?: (league: Clubhouse_LeagueOperatorDashboardLeagueDto) => void;
}

/**
 * Rows for one view's leagues, sorted by that view's comparator, then paged.
 *
 * Everything a row shows now comes off the dashboard payload itself. This
 * used to fetch the round list for every league in the bucket to find its
 * open/next/last-finalized windows; the dashboard DTO carries them as
 * `roundSchedule`, so there is nothing left to wait on here.
 */
export function DashboardBucketList({
  view,
  leagues,
  page,
  pageSize,
  onPageChange,
  onOpen,
  onDelete,
}: DashboardBucketListProps) {
  const comparator =
    view === "IN_SEASON"
      ? compareInSeason
      : view === "UPCOMING"
        ? compareUpcoming
        : compareFinished;
  const sorted = useMemo(
    () => [...leagues].sort(comparator),
    [leagues, comparator],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const pageItems = sorted.slice(
    (clampedPage - 1) * pageSize,
    clampedPage * pageSize,
  );

  return (
    <>
      <div className={`mt-2 ${DASHBOARD_CARD_CLASS}`}>
        <div className="divide-y divide-border/50">
          {pageItems.map((league) => (
            <LeagueRow
              key={league.leagueId}
              league={league}
              view={view}
              onOpen={onOpen}
              onDelete={onDelete}
            />
          ))}
        </div>
      </div>
      <Pager
        page={clampedPage}
        pageSize={pageSize}
        totalItems={sorted.length}
        onPageChange={onPageChange}
      />
    </>
  );
}
